import { useMemo, useState } from 'react'
import { Area, AreaChart, Legend, Tooltip, XAxis, YAxis } from 'recharts'
import { ResponsiveContainer } from 'recharts'

import { Coupon, CouponPayment } from '@/api/affiliate/types/Coupon'
import { PkgWarehouse } from '@/api/user-package/types/UserPkg'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { WarehousePreview } from '@/components/WarehousePreview'
import { cn } from '@/lib/utils'

const calcRevenueFromPayment = (payment: CouponPayment, coupon: Coupon) => {
  const couponRevenue = payment.couponRevenue ?? (coupon.revenue ? parseFloat(coupon.revenue) : 0)
  return couponRevenue * ((payment.shippingPaid || 0) - (payment.refundAmount || 0))
}
interface AffiliateCouponCardProps {
  coupons: Coupon[]
  allowDailyChart?: boolean
}
export const AffiliateCouponSection = ({ coupons, allowDailyChart = false }: AffiliateCouponCardProps) => {
  const [isDailyView, setIsDailyView] = useState(false)
  // Get coupon codes for chart areas
  const couponCodes = useMemo(() => {
    return coupons?.map(coupon => coupon.code) || []
  }, [coupons])
  const couponByCodeByCountry = useMemo(() => {
    if (!coupons) return []
    return coupons.map(coupon => {
      const couponContries = coupon.country
        .filter(country => country !== 'all')
        .reduce(
          (acc, country) => {
            acc[country] = []
            return acc
          },
          {} as Record<string, CouponPayment[]>
        )
      // Group payments by warehouse/country
      const paymentsByCountry =
        coupon.payments?.reduce((acc, payment) => {
          const warehouse = payment.warehouse
          if (!warehouse) return acc
          if (!acc[warehouse]) {
            acc[warehouse] = []
          }
          acc[warehouse].push(payment)
          return acc
        }, couponContries) || couponContries

      // Calculate total usage and revenue across all countries
      const totalUsage = coupon.payments?.length || 0
      const totalRevenue = coupon.payments?.reduce((sum, payment) => sum + calcRevenueFromPayment(payment, coupon), 0) || 0

      // Create array starting with total row
      const result: Array<{
        couponCode: string
        country: string | null
        payments: CouponPayment[]
        usage: number
        revenue: number
        couponRevenue: string
        couponCountries: (PkgWarehouse | 'all')[]
        isTotal: boolean
      }> = [
        {
          couponCode: coupon.code,
          country: null,
          payments: coupon.payments || [],
          usage: totalUsage,
          revenue: totalRevenue,
          couponRevenue: coupon.revenue,
          couponCountries: coupon.country,
          isTotal: true,
        },
      ]

      // Add per-country rows only if there are multiple countries
      const countryEntries = Object.entries(paymentsByCountry)
      if (countryEntries.length > 1) {
        countryEntries.forEach(([country, payments]) => {
          // Calculate revenue for this country
          const countryRevenue = payments.reduce((sum, payment) => sum + calcRevenueFromPayment(payment, coupon), 0)

          result.push({
            couponCode: coupon.code,
            country,
            payments,
            usage: payments.length,
            revenue: countryRevenue,
            couponRevenue: coupon.revenue,
            couponCountries: coupon.country,
            isTotal: false,
          })
        })
      }

      return result
    })
  }, [coupons])
  // Color palette for coupon codes
  const couponColors = ['#8884d8', '#20c997', '#ffc658', '#ff7300', '#82ca9d', '#ff6b6b']
  // Process payments data for chart - group by coupon code and month or day
  const chartData = useMemo(() => {
    if (!coupons || coupons.length === 0) return []

    // Get all unique time periods (months or days) across all coupons
    const allTimePeriods = new Set<string>()
    const couponDataMap: Record<string, Record<string, { usage: number; revenue: number }>> = {}

    coupons.forEach(coupon => {
      if (coupon.payments && Array.isArray(coupon.payments)) {
        const couponCode = coupon.code
        if (!couponDataMap[couponCode]) {
          couponDataMap[couponCode] = {}
        }

        coupon.payments.forEach((payment: CouponPayment) => {
          if (payment.date) {
            // Extract time period key based on view mode
            // Daily: YYYY-MM-DD from date string (e.g., "2025-05-24T00:00:00.000Z" -> "2025-05-24")
            // Monthly: YYYY-MM from date string (e.g., "2025-05-24T00:00:00.000Z" -> "2025-05")
            const timeKey = isDailyView ? payment.date.substring(0, 10) : payment.date.substring(0, 7)
            allTimePeriods.add(timeKey)

            if (!couponDataMap[couponCode][timeKey]) {
              couponDataMap[couponCode][timeKey] = { usage: 0, revenue: 0 }
            }

            // Calculate revenue: couponRevenue * shippingPaid or use revenue field
            const revenue = calcRevenueFromPayment(payment, coupon)
            couponDataMap[couponCode][timeKey].usage += 1
            couponDataMap[couponCode][timeKey].revenue += revenue
          }
        })
      }
    })

    // Create chart data with all time periods and coupon codes
    const sortedTimePeriods = Array.from(allTimePeriods).sort()
    return sortedTimePeriods.map(timePeriod => {
      const dataPoint: Record<string, string | number> = { date: timePeriod }
      Object.keys(couponDataMap).forEach(couponCode => {
        const couponData = couponDataMap[couponCode][timePeriod]

        if (couponData) {
          dataPoint[`${couponCode}_usage`] = couponData.usage
          dataPoint[`${couponCode}_revenue`] = Math.round(couponData.revenue)
        } else {
          dataPoint[`${couponCode}_usage`] = 0
          dataPoint[`${couponCode}_revenue`] = 0
        }
      })
      return dataPoint
    })
  }, [coupons, isDailyView])

  return (
    <Card className='bg-white'>
      <CardHeader>
        <CardTitle className='text-text-primary text-xl font-medium'>פירוט קופונים</CardTitle>
      </CardHeader>
      <CardContent className='space-y-4'>
        {coupons && coupons.length > 0 ? (
          <>
            <div className='border-border-default overflow-hidden rounded-lg border px-3'>
              <div className='border-border-default text-text-secondary grid grid-cols-3 gap-4 border-b py-3'>
                <div className='text-sm font-medium'>שם קופון</div>
                <div className='text-center text-sm font-medium'>שימושים</div>
                <div className='text-end text-sm font-medium'>רווח</div>
              </div>
              {couponByCodeByCountry.map((couponRows, couponIdx) => {
                const hasMultipleCountries = couponRows.filter(r => !r.isTotal).length > 1
                return (
                  <div key={couponIdx} className='border-border-default border-b py-3 last:border-0'>
                    {couponRows.map((row, rowIdx) => {
                      // Only show per-country rows if there are multiple countries
                      if (!row.isTotal && !hasMultipleCountries) {
                        return null
                      }

                      return (
                        <div key={rowIdx} className={`grid grid-cols-3 gap-4 ${!row.isTotal ? 'mt-2' : ''}`}>
                          <div className='flex items-center gap-2 text-sm'>
                            <div className='flex items-center -space-x-2'>
                              {row.isTotal
                                ? // Show all countries for total row
                                  row.couponCountries.includes('all')
                                  ? Object.values(PkgWarehouse).map(country => (
                                      <WarehousePreview
                                        className='border-border-default rounded-full border'
                                        key={country}
                                        warehouse={country}
                                      />
                                    ))
                                  : row.couponCountries.map(country => (
                                      <WarehousePreview key={country} warehouse={country as PkgWarehouse} />
                                    ))
                                : // Show single country for per-country row
                                  row.country && (
                                    <WarehousePreview
                                      containerClassName='text-text-secondary'
                                      includeLabel
                                      warehouse={row.country as PkgWarehouse}
                                    />
                                  )}
                            </div>
                            {row.isTotal ? row.couponCode : ''}
                          </div>
                          <div className={cn('text-center text-sm', !row.isTotal && 'text-text-secondary')}>{row.usage}</div>
                          <div className={cn('text-end text-sm', !row.isTotal && 'text-text-secondary')}>{Math.round(row.revenue)}₪</div>
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </div>
            {/* Chart Section */}
            {chartData.length > 0 && couponCodes.length > 0 && (
              <div className='mt-6'>
                <div className='mb-4 flex items-center justify-between'>
                  <div className='text-text-primary text-lg font-medium'>גרף שימוש בקופונים לאורך זמן</div>
                  {allowDailyChart && (
                    <div className='flex items-center gap-2'>
                      <label htmlFor='daily-chart-toggle' className='text-text-secondary cursor-pointer text-sm'>
                        חודשית
                      </label>
                      <Switch id='daily-chart-toggle' checked={isDailyView} onCheckedChange={setIsDailyView} />
                      <label htmlFor='daily-chart-toggle' className='text-text-secondary cursor-pointer text-sm'>
                        יומית
                      </label>
                    </div>
                  )}
                </div>
                <div dir='ltr' className='bg-white'>
                  <ResponsiveContainer width='100%' height={350}>
                    <AreaChart data={chartData}>
                      <defs>
                        {couponCodes.map((code, idx) => {
                          const color = couponColors[idx % couponColors.length]
                          return (
                            <linearGradient key={code} id={`color${code}`} x1='0' y1='0' x2='0' y2='1'>
                              <stop offset='5%' stopColor={color} stopOpacity={0.8} />
                              <stop offset='95%' stopColor={color} stopOpacity={0} />
                            </linearGradient>
                          )
                        })}
                      </defs>
                      <XAxis
                        dataKey='date'
                        tick={{ fontSize: 12 }}
                        tickFormatter={value => {
                          if (isDailyView) {
                            const date = new Date(value)
                            return date.toLocaleDateString('he-IL', { day: 'numeric', month: 'short' })
                          } else {
                            const date = new Date(value + '-01') // Add day to make valid date
                            return date.toLocaleDateString('he-IL', { month: 'short', year: '2-digit' })
                          }
                        }}
                      />
                      <YAxis
                        type='number'
                        tick={{ fontSize: 12 }}
                        width={50}
                        label={{ value: 'שימושים', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                      />
                      <Tooltip
                        labelFormatter={label => {
                          if (isDailyView) {
                            const date = new Date(label)
                            return date.toLocaleDateString('he-IL', { year: 'numeric', month: 'long', day: 'numeric' })
                          } else {
                            const date = new Date(label + '-01') // Add day to make valid date
                            return date.toLocaleDateString('he-IL', { year: 'numeric', month: 'long' })
                          }
                        }}
                        content={({ active, payload, label }) => {
                          if (!active || !payload || !payload.length) return null

                          const couponData: Record<string, { usage: number; revenue: number }> = {}
                          const items = payload[0].payload
                          Object.entries(items).forEach(([key, value]) => {
                            const match = key.match(/^(.+)_(usage|revenue)$/)
                            if (match) {
                              const [, couponCode, type] = match
                              if (!couponData[couponCode]) {
                                couponData[couponCode] = { usage: 0, revenue: 0 }
                              }
                              if (type === 'usage') {
                                couponData[couponCode].usage = Number(value) || 0
                              }

                              if (type === 'revenue') {
                                couponData[couponCode].revenue = Number(value) || 0
                              }
                            }
                          })

                          let formattedDate: string
                          if (isDailyView) {
                            const date = new Date(label)
                            formattedDate = date.toLocaleDateString('he-IL', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })
                          } else {
                            const date = new Date(label + '-01') // Add day to make valid date
                            formattedDate = date.toLocaleDateString('he-IL', {
                              year: 'numeric',
                              month: 'long',
                            })
                          }

                          return (
                            <div className='border-border-default rounded-lg border bg-white p-3 shadow-lg'>
                              <div className='text-text-primary mb-2 font-medium'>{formattedDate}</div>
                              {Object.entries(couponData).map(([code, data]) => (
                                <div key={code} className='mb-1 text-sm'>
                                  <div className='text-text-primary font-medium'>{code}</div>
                                  <div className='text-text-secondary'>שימושים: {data.usage}</div>
                                  {data.revenue > 0 && <div className='text-text-secondary'>רווח: {data.revenue}₪</div>}
                                </div>
                              ))}
                            </div>
                          )
                        }}
                      />
                      <Legend
                        iconType='line'
                        className='flex items-center'
                        formatter={value => {
                          const match = value.match(/^(.+)_usage$/)
                          return match ? match[1] : value
                        }}
                      />
                      {couponCodes.map((code, idx) => {
                        const color = couponColors[idx % couponColors.length]
                        return (
                          <Area
                            key={code}
                            type='monotone'
                            dataKey={`${code}_usage`}
                            name={`${code}_usage`}
                            stroke={color}
                            strokeWidth={2}
                            fillOpacity={0.6}
                            fill={`url(#color${code})`}
                          />
                        )
                      })}
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className='text-text-secondary py-4 text-center'>אין קופונים להצגה</div>
        )}
      </CardContent>
    </Card>
  )
}
